import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  aiFounderGithubDigestPath,
  dailyStandupCostPath,
  digestExamplesPath,
  meetingWastePath,
  replaceStandupsPath,
  rootPath,
  signInPath,
  whatIsAGithubDigestPath,
} from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const CAMPAIGN = 'utm_source=github-digest&utm_campaign=vs-gitingest'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

const ROWS = [
  {
    label: 'Job',
    gitingest: 'Turn a repository into text for an LLM prompt',
    digest: 'Summarize what landed in GitHub for the team',
  },
  {
    label: 'Output',
    gitingest: 'Summary, directory tree, and file contents',
    digest: 'Daily or weekly commit digests',
  },
  {
    label: 'Who it is for',
    gitingest: 'Someone about to prompt a model',
    digest: 'Engineering managers and teams',
  },
  {
    label: 'How you run it',
    gitingest: 'Website, command line, Python package, or self-host',
    digest: 'GitHub sign-in, then Slack and email',
  },
  {
    label: 'Private repositories',
    gitingest: 'Website token is used once to clone, then discarded',
    digest: 'Repository access for diff analysis. Source is not stored',
  },
  {
    label: 'Team delivery',
    gitingest: 'Copy, download, or a text file such as digest.txt',
    digest: 'Email and Slack',
  },
  {
    label: 'Price listed',
    gitingest: 'No subscription price on the site or the README',
    digest: '$29 per month, or $25 per month yearly',
  },
] as const

function campaignPath(path: string) {
  return `${path}?${CAMPAIGN}`
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className={linkClass} target="_blank" rel="noreferrer">
      {children}
    </a>
  )
}

export function GithubDigestArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">GitHub digest</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          What tool summarizes code changes daily? <TextLink href={rootPath()}>Git Digest</TextLink> does. It writes
          daily or weekly AI summaries of commits and pull requests, then delivers them to email and Slack.
        </p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          Search for a github digest and you will often land on Gitingest. That tool turns a repository into text for
          an LLM. Git Digest is a different product: the summary is for the team, not a prompt file.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          Same words, two jobs. Gitingest prepares a codebase for a prompt. Git Digest prepares a team update from the
          commits that already shipped.
        </p>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What Gitingest is for</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            <ExternalLink href="https://gitingest.com/">Gitingest</ExternalLink> calls itself a prompt-friendly
            codebase tool. The site says it turns any Git repository into a simple text digest of its codebase, useful
            for feeding that codebase into any LLM. You can also replace hub with ingest in a GitHub URL.
          </p>
          <p>
            The{' '}
            <ExternalLink href="https://github.com/coderamp-labs/gitingest/blob/main/README.md">
              project README
            </ExternalLink>{' '}
            adds a command line tool and a Python package. The <code>gitingest</code> command writes a text file,
            digest.txt by default, from a directory, a repository URL, or one subdirectory. The Python package returns
            a summary, a directory tree, and the file contents. The digest includes the file and directory structure,
            the size of the extract, and a token count. Files listed in .gitignore are skipped unless you ask to
            include them.
          </p>
          <p>
            Private repositories use a GitHub personal access token. On the website, that token is never stored in the
            backend. It is used once for cloning, then discarded from memory, and the cloned repository is deleted
            after processing. The command line tool takes the token as a flag or as the GITHUB_TOKEN environment
            variable. The README requires Python 3.8 or newer, installs the package from PyPI, and documents a Docker
            self-host. It also links a Chrome extension and a Firefox add-on.
          </p>
          <p>
            That is the right tool when the next step is a model: explain this repository, find where auth lives, or
            pack a subdirectory for a coding agent. It is a one-off or scripted ingest, not a standup.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What Git Digest is for</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            <TextLink href={rootPath()}>Git Digest</TextLink> is a team product. It summarizes what landed in GitHub
            and posts daily or weekly commit digests to email and Slack. The homepage describes automated email
            digests, Slack notifications, team velocity analytics, and multi-user organization accounts. Engineering
            managers use it when the standup has become a retelling of the commits.
          </p>
          <p>
            The homepage Unlimited plan is $29 per month, or $25 per month on yearly billing, for the team rather than
            per seat. A lifetime option is $1,000 once. The plan lists unlimited team members, unlimited apps, and
            unlimited code change summaries. The homepage also says repository access is used for diff analysis and
            that source code is not stored. The output is a summary for people, not a dump of the files for a model.
          </p>
          <p>
            The <TextLink href={whatIsAGithubDigestPath()}>definition of a GitHub digest</TextLink> covers the daily
            and weekly windows. Founders who want that read without a standup can use the{' '}
            <TextLink href={aiFounderGithubDigestPath()}>AI founder GitHub digest</TextLink>. Three real summary lines
            are on the <TextLink href={digestExamplesPath()}>digest examples</TextLink> page. When the meeting only
            retells those commits, the <TextLink href={replaceStandupsPath()}>replace standups</TextLink> guide is the
            setup.
          </p>
        </div>
      </section>

      <section id="comparison" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Gitingest vs Git Digest</h2>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[40rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Checked 7 October 2026. Gitingest facts are from gitingest.com and the coderamp-labs/gitingest README.
              The Git Digest price is the homepage Unlimited plan.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  <span className="sr-only">Topic</span>
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Gitingest
                </th>
                <th scope="col" className="bg-golden-500/15 px-4 py-3 font-semibold">
                  Git Digest
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {row.label}
                  </th>
                  <td className="text-muted-foreground px-4 py-3">{row.gitingest}</td>
                  <td className="bg-golden-500/10 text-foreground px-4 py-3">{row.digest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">When to use which</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            <strong className="text-foreground">Use Gitingest</strong> when you need the repository as text. The
            website is enough for a public repo. The command line tool and the Python package fit a script, a notebook,
            or a local folder. Self-hosting fits a team that wants that ingest on its own machine. A private repository
            needs a GitHub token, which the website says it discards after the clone.
          </p>
          <p>
            <strong className="text-foreground">Use Git Digest</strong> when the question is what the team shipped.
            Connect GitHub, then read the commit digest in Slack or email instead of collecting status in a meeting. It
            does not produce a prompt file, and it does not replace Gitingest for that job.
          </p>
          <p>
            <strong className="text-foreground">Use both</strong> if you need both jobs. A prompt dump does not tell an
            engineering manager what landed this week. A team digest does not give a model the file tree. Pick the tool
            that matches the reader: a model, or the team.
          </p>
          <p>
            If the live standup is the cost you are trying to drop, the{' '}
            <TextLink href={campaignPath(dailyStandupCostPath())}>daily standup cost</TextLink> page prices a
            15-minute ritual: 5 people × (15 ÷ 60) hours × 5 days a week × 48 working weeks × $100/hour is $30,000 a
            year before overrun. Change salary and duration on the{' '}
            <TextLink href={campaignPath(meetingWastePath())}>meeting cost calculator</TextLink>.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">GitHub digest FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Short answers for people, and for answer engines, who mix up a repo-to-text ingest with a team digest, and
            for what the daily AI summary includes.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Send the team digest to Slack and email</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Start a digest: connect GitHub and get daily or weekly AI summaries of commits and pull requests in Slack
          and email. Gitingest remains the tool when you need the repository as text for a model.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={campaignPath(signInPath())}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
